import { useBehavior, useButton, useDetectDocumentScroll, useTriggerAction } from '@libs/core';
import { TButtonData, TWidget } from '@libs/types';
import React from 'react';
import { TButtonOption } from '../../types/options';
import { ButtonPillView, TButtonPillViewProps } from './ButtonPillView';

type TButtonPillWidgetProps = {
    widget: TWidget<TButtonData, TButtonOption>;
};

/**
 * Standalone ButtonPill widget driven entirely by server-provided configuration.
 * Owns behavior hooks (useBehavior, useButton, useDetectDocumentScroll) and
 * delegates all rendering to ButtonPillView.
 *
 * Use this component when the widget definition comes from the server and no
 * additional style overrides are required.
 */
export const ButtonPillWidget = ({ widget }: TButtonPillWidgetProps) => {
    const { isDisabled, isHidden } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { handleClick } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
    const { documentScrolled } = useDetectDocumentScroll(widget.uid);

    const label = widget.data?.messages?.label ?? widget.data?.label;
    const parentStyleNames = [widget.options?.style ?? ' '].filter(Boolean) as string[];

    return (
        <ButtonPillView
            uid={widget.uid}
            widget={widget as TButtonPillViewProps['widget']}
            label={label}
            hierarchy={widget.options?.hierarchy}
            iconName={widget.data?.iconName}
            flagIcon={widget.data?.flagIcon}
            iconPosition={widget.options?.iconPosition}
            size={widget.options?.size}
            position={widget.options?.position}
            dynamicShadow={widget.options?.dynamicShadow}
            fillContainer={widget.options?.fillContainer}
            margin={widget.options?.margin}
            documentScrolled={documentScrolled}
            isDisabled={isDisabled}
            isHide={isHidden}
            parentStyleNames={parentStyleNames}
            onClick={handleClick}
        />
    );
};
